// src/markup/pages/programmatic-hub-page.jsx
// Hub pages for the programmatic link graph:
//   L1  /{service}            → lists brands + popular city leaves
//   L2  /{service}/:brand     → lists every city for that brand+service
// These also resolve the leaf pages' breadcrumb parents (previously soft-404s).
// Pure + static-data driven (no fetch / no window in render) → SSR-safe.

import React, { useState, useEffect } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Header from '../layout/header';
import Footer from '../layout/footer';
import { Breadcrumbs, ProgrammaticLinkHub, FinalCTA, StructuredData, SectionWrapper, FaqAccordion } from '../elements/premium';
import { getServiceStartingPrice } from '../../utils/pricing';
import { readInjectedLinkData, consumeInjectedLinkData, fetchLinkData } from '../../utils/linkHubData';

const BASE = 'https://www.ridenrepair.com';

// Service labels (so the hero/title render without waiting on the link payload).
const SERVICE_LABELS = {
  'bike-service': 'Bike Service', 'bike-repair': 'Bike Repair', 'bike-oil-change': 'Bike Oil Change',
  'car-service': 'Car Service', 'car-repair': 'Car Repair', 'car-ac-repair': 'Car AC Repair',
  'car-battery': 'Car Battery Replacement',
};
const titleCase = (s) => (s || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Per-service "what's included" scope + turnaround — gives the hub pages
// genuine, service-specific substance (not just a link grid → not a doorway).
const SERVICE_CONTENT = {
  'bike-service': { turnaround: '60–90 minutes', scope: ['Engine oil change with OEM-grade oil', 'Air filter clean & replacement', 'Chain lubrication & tension adjustment', 'Spark plug inspection', 'Brake pad & caliper check', 'Battery & electrical check'] },
  'bike-repair': { turnaround: '90–150 minutes', scope: ['Full diagnostic & fault-code read', 'Clutch & cable inspection', 'Carburettor/fuel-injector service', 'Electrical & starter check', 'Suspension & brake repair', 'Drive chain & sprocket analysis'] },
  'bike-oil-change': { turnaround: '30–45 minutes', scope: ['Drain used engine oil', 'Replace oil filter / clean strainer', 'Refill manufacturer-spec grade', 'Reset service reminder', 'Torque-check drain bolt'] },
  'car-service': { turnaround: '2–3 hours', scope: ['Engine oil & filter replacement', 'Air, cabin & AC filter change', 'Coolant top-up & hose check', 'Brake fluid & pad audit', 'Battery & alternator test', '50-point multi-check & road test'] },
  'car-repair': { turnaround: '3–5 hours', scope: ['Live OBD2 scan with freeze-frame', 'Engine-bay & leak-down test', 'Suspension & strut inspection', 'Brake caliper/rotor/pad work', 'Alternator & battery load test', 'Cooling-system pressure test'] },
  'car-ac-repair': { turnaround: '90–180 minutes', scope: ['AC gas check & refill (R134a / R1234yf)', 'Compressor engagement & cut-in test', 'Condenser & evaporator fin clean', 'Cabin & AC filter replacement', 'Cooling-vent temperature test', 'Leak detection with UV dye'] },
  'car-battery': { turnaround: '30–60 minutes', scope: ['Battery voltage & CCA load test', 'Alternator output measurement', 'Terminal & harness corrosion clean', 'New battery fitment with ECU memory-save', 'Old battery eco-disposal'] },
};

// Service-specific FAQ set (brand-aware) — rendered with FAQPage schema.
function buildHubFaqs(service, serviceLabel, brandDisplay, startLabel, cityCount) {
  const lc = serviceLabel.toLowerCase();
  const who = brandDisplay ? `${brandDisplay} ` : '';
  const cc = SERVICE_CONTENT[service] || {};
  return [
    {
      q: `How much does ${who}${lc} cost?`,
      a: `${who}${lc} starts at ${startLabel}. The final price depends on your ${brandDisplay || 'vehicle'} variant and any parts needed — you'll see the exact quote on the booking screen before you confirm, with no hidden charges.`,
    },
    {
      q: `How long does ${who}${lc} take at my doorstep?`,
      a: `Most ${who}${lc} jobs are completed in about ${cc.turnaround || '60–90 minutes'} at your home or office. A certified mechanic arrives within 15 minutes of your slot, and you only pay once the work is done.`,
    },
    {
      q: `Is doorstep ${lc} as reliable as a workshop?`,
      a: `Yes. Our certified${brandDisplay ? `, ${brandDisplay}-trained` : ''} mechanics bring professional tools and genuine parts to your doorstep, and every job carries a 30-day labour warranty plus a digital job card with before-and-after photos.`,
    },
    {
      q: `Which cities is ${who}${lc} available in?`,
      a: `${who}${lc} is available across ${cityCount}+ cities in India. Pick your city above to book a doorstep slot — same-day and weekend slots are available subject to mechanic availability.`,
    },
  ];
}

const HubServiceContent = ({ service, serviceLabel, brandDisplay, startLabel, cityCount }) => {
  const cc = SERVICE_CONTENT[service];
  const faqs = buildHubFaqs(service, serviceLabel, brandDisplay, startLabel, cityCount);
  const lc = serviceLabel.toLowerCase();
  const who = brandDisplay ? `${brandDisplay} ` : '';
  return (
    <>
      {cc && cc.scope && cc.scope.length > 0 && (
        <SectionWrapper variant="gray" kicker="What's Included" heading={`What's Covered in ${who}${serviceLabel}`}>
          <p className="rr-prog__lead">{`Every ${who}${lc} booking with Ride N Repair is a complete, transparent job done at your doorstep — genuine parts, certified mechanics and a 30-day labour warranty, from ${startLabel}.`}</p>
          <div className="rr-prog__scope-list">
            {cc.scope.map((item, i) => (<div key={i} className="rr-prog__scope-item">{item}</div>))}
          </div>
        </SectionWrapper>
      )}
      <SectionWrapper narrow kicker="Common Questions" heading={`${who}${serviceLabel} — FAQ`}>
        <FaqAccordion faqs={faqs} />
      </SectionWrapper>
    </>
  );
};

const ProgrammaticHubPage = (props) => {
  const { service, isMobileBrowser, isMobileApp, user, setUser, setCitiesModal } = props;
  const brandSlug = (props.match && props.match.params && props.match.params.brand) || null;

  const kind = brandSlug ? 'l2' : 'service';
  const key = brandSlug ? `${service}/${brandSlug}` : service;

  // Link data is SSR-injected (window.__LINK_HUB_DATA__) on first load and
  // fetched on SPA nav — never bundled. Seed from injection for hydration match.
  const [state, setState] = useState(() => {
    const inj = readInjectedLinkData(kind, key);
    return { payload: inj || null, loaded: !!inj };
  });
  useEffect(() => {
    const inj = consumeInjectedLinkData(kind, key);
    if (inj) { setState({ payload: inj, loaded: true }); return; }
    setState({ payload: null, loaded: false });
    const url = brandSlug ? `/_data/links/hub/${service}/${brandSlug}` : `/_data/links/hub/${service}`;
    let live = true;
    fetchLinkData(url).then((p) => { if (live) setState({ payload: p || null, loaded: true }); });
    return () => { live = false; };
  }, [service, brandSlug, kind, key]);

  const serviceLabel = SERVICE_LABELS[service];
  if (!serviceLabel) return <Redirect to="/error-404" />;
  const vt = service.indexOf('car') === 0 ? 'car' : 'bike';
  const sp = getServiceStartingPrice(service, vt);
  const startLabel = sp.label || '';
  const { payload, loaded } = state;

  // ── L2: /{service}/:brand → cities for this brand+service ────────────
  if (brandSlug) {
    if (loaded && !payload) return <Redirect to="/error-404" />;
    const brandDisplay = (payload && payload.brandDisplay) || titleCase(brandSlug);
    const cityLinks = (payload && payload.cities) || [];
    const svc = { label: serviceLabel };
    const h1 = `${brandDisplay} ${svc.label} at Your Doorstep`;
    const canonical = `${BASE}/${service}/${brandSlug}`;
    const metaDesc = `Book ${brandDisplay} ${svc.label.toLowerCase()} at your doorstep — certified mechanics, genuine parts, 30-day warranty, from ${startLabel}. Choose your city.`;
    const crumbs = [
      { label: 'Home', href: '/' },
      { label: svc.label, href: `/${service}` },
      { label: brandDisplay },
    ];
    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{`${brandDisplay} ${svc.label} — Doorstep in Your City | Ride N Repair`}</title>
          <meta name="description" content={metaDesc} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={`${brandDisplay} ${svc.label} | Ride N Repair`} />
          <meta property="og:description" content={metaDesc} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="website" />
        </Helmet>
        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />
        <Breadcrumbs items={crumbs} />
        <div className="rr-hubhero">
          <div className="rr-container">
            <span className="rr-hubhero__kicker">Doorstep Service</span>
            <h1 className="rr-hubhero__title">{h1}</h1>
            <p className="rr-hubhero__sub">{`Certified ${brandDisplay} mechanics at your home or office across India — genuine parts, 30-day labour warranty, from ${startLabel}.`}</p>
            <Link to="/service" className="rr-cta-primary">{`Book ${svc.label} — ${startLabel} Onwards`}</Link>
          </div>
        </div>
        <ProgrammaticLinkHub
          heading={`${brandDisplay} ${svc.label} — Choose Your City`}
          subheading={`Doorstep ${brandDisplay} ${svc.label.toLowerCase()} available across ${cityLinks.length} cities.`}
          links={cityLinks}
        />
        <HubServiceContent
          service={service}
          serviceLabel={svc.label}
          brandDisplay={brandDisplay}
          startLabel={startLabel}
          cityCount={cityLinks.length}
        />
        <FinalCTA
          heading={<>Book {brandDisplay} {svc.label}<br />at Your Doorstep Today.</>}
          subtitle={`Doorstep service · Certified mechanics · ${startLabel} onwards · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />
        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }

  // ── L1: /{service} → brands + popular city leaves ────────────────────
  const data = (payload && payload.data) || {};
  const svc = { label: serviceLabel, brands: data.brands || [], leaves: data.leaves || [] };
  const h1 = `${svc.label} at Your Doorstep`;
  const canonical = `${BASE}/${service}`;
  const metaDesc = `Book ${svc.label.toLowerCase()} at your doorstep across India — certified mechanics, genuine parts, 30-day warranty, from ${startLabel}. Pick your brand and city.`;
  const crumbs = [
    { label: 'Home', href: '/' },
    { label: svc.label },
  ];
  const brandHubList = {
    '@type': 'ItemList',
    itemListElement: (svc.brands || []).map((b, i) => ({
      '@type': 'ListItem', position: i + 1, url: `${BASE}${b.url}`, name: `${b.display} ${svc.label}`,
    })),
  };
  return (
    <div className="rr-home rr-home--lux">
      <Helmet>
        <title>{`${svc.label} at Your Doorstep — From ${startLabel} | Ride N Repair`}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${svc.label} at Your Doorstep | Ride N Repair`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
      </Helmet>
      <StructuredData data={brandHubList} />
      <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />
      <Breadcrumbs items={crumbs} />
      <div className="rr-hubhero">
        <div className="rr-container">
          <span className="rr-hubhero__kicker">Doorstep Service</span>
          <h1 className="rr-hubhero__title">{h1}</h1>
          <p className="rr-hubhero__sub">{`Certified mechanics at your home or office — genuine parts, 30-day labour warranty, from ${startLabel}. Choose your brand below.`}</p>
          <Link to="/service" className="rr-cta-primary">{`Book ${svc.label} — ${startLabel} Onwards`}</Link>
        </div>
      </div>

      {/* Browse by brand → L2 hub pages */}
      {svc.brands && svc.brands.length > 0 && (
        <section className="rr-linkhub">
          <div className="rr-container">
            <h2 className="rr-linkhub__heading">{`${svc.label} by Brand`}</h2>
            <p className="rr-linkhub__sub">Pick your brand to see doorstep availability in your city.</p>
            <div className="rr-linkhub__group">
              <div className="rr-linkhub__pills">
                {svc.brands.map((b, i) => (
                  <Link key={i} to={b.url} className="rr-linkhub__pill">{b.display}</Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Popular brand × city leaf links */}
      {svc.leaves && svc.leaves.length > 0 && (
        <ProgrammaticLinkHub
          heading={`Popular ${svc.label} Bookings`}
          subheading={`Doorstep ${svc.label.toLowerCase()} across India's top cities.`}
          links={svc.leaves}
        />
      )}

      <HubServiceContent
        service={service}
        serviceLabel={svc.label}
        brandDisplay={''}
        startLabel={startLabel}
        cityCount={(svc.leaves && svc.leaves.length) ? 29 : 0}
      />

      <FinalCTA
        heading={<>Book {svc.label}<br />at Your Doorstep Today.</>}
        subtitle={`Doorstep service · Certified mechanics · ${startLabel} onwards · 30-day warranty`}
        ctas={[
          { label: 'Book Now', href: '/service', variant: 'primary' },
          { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
        ]}
      />
      <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
    </div>
  );
};

export default ProgrammaticHubPage;
